<template>
  <div class="host-ip-input">
    <!-- Label + Toggle -->
    <div class="d-flex align-items-center justify-content-between">
      <label class="mr-1 mb-0 font-weight-bold" style="white-space: nowrap">
        {{ label }}
      </label>
      <div class="d-flex align-items-center" style="gap: 0.4rem">
        <small class="mr-50" :class="{ 'font-weight-bold': !useHostMode }">IP</small>
        <b-form-checkbox
          v-model="useHostMode"
          switch
          size="sm"
          class="mb-0 mx-0"
          style="position: relative; top: 4px"
        />
        <small :class="{ 'font-weight-bold': useHostMode }">Host</small>
      </div>
    </div>

    <!-- Host mode: text field (e.g. example.com:55555) -->
    <div v-if="useHostMode">
      <b-form-input
        v-model="hostValue"
        :placeholder="showPort ? 'example.com:1234' : 'example.com'"
        :state="hostValue ? null : false"
      />
      <small v-if="!hostValue" class="text-danger">{{ $t("dmp.required") }}</small>
    </div>

    <!-- IP mode: IP + optional Port -->
    <div v-else class="d-flex align-items-start" style="gap: 0.5rem">
      <div style="flex: 1">
        <ip-address-input :value="ipValue" @input="onIpChange" />
      </div>
      <div v-if="showPort" style="width: 100px">
        <b-form-input
          v-model="portValue"
          placeholder="Puerto"
          type="number"
          min="1"
          max="65535"
          :state="portValue ? Number(portValue) >= 1 && Number(portValue) <= 65535 : null"
          required
          size="md"
        />
        <small v-if="!portValue" class="text-danger">{{ $t("dmp.required") }}</small>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, nextTick } from "@vue/composition-api";
import IpAddressInput from "@/components/InputIp/IpAddressInput.vue";

export default {
  name: "HostIpInput",
  components: { IpAddressInput },
  props: {
    value: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "Host",
    },
    showPort: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const ipPattern =
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

    // --- Parse the initial value ---
    const existingHost = props.value || "";
    const parts = existingHost.split(":");
    const looksLikeIp = ipPattern.test(parts[0]);

    const useHostMode = ref(!looksLikeIp);
    const hostValue = ref(!looksLikeIp ? existingHost : "");
    const ipValue = ref(looksLikeIp ? parts[0] : "");
    const portValue = ref(looksLikeIp && parts[1] ? parts[1] : "");

    // --- Flag to distinguish self-emitted changes from external ones ---
    let isInternalChange = false;

    // --- Emit combined value ---
    const emitValue = () => {
      isInternalChange = true;
      if (useHostMode.value) {
        emit("input", hostValue.value);
      } else {
        if (props.showPort && ipValue.value && portValue.value) {
          emit("input", ipValue.value + ":" + portValue.value);
        } else {
          emit("input", ipValue.value);
        }
      }
      // Safety net: clear the flag on nextTick in case the watcher
      // doesn't fire (e.g. emitted value equals the current prop)
      nextTick(() => {
        isInternalChange = false;
      });
    };

    // --- Sync when external value changes (e.g. loading edit data) ---
    watch(
      () => props.value,
      (val) => {
        if (isInternalChange) {
          isInternalChange = false;
          return;
        }
        const raw = val || "";
        const p = raw.split(":");
        const isIp = ipPattern.test(p[0]);

        if (isIp) {
          useHostMode.value = false;
          ipValue.value = p[0];
          portValue.value = p[1] || "";
        } else if (raw) {
          useHostMode.value = true;
          hostValue.value = raw;
        } else {
          // Empty value → full reset (e.g. sidebar reopened for a new item)
          useHostMode.value = false;
          ipValue.value = "";
          portValue.value = "";
          hostValue.value = "";
        }
      },
      { immediate: true }
    );

    // --- When toggling mode, re-parse current value ---
    watch(useHostMode, (isHost) => {
      if (!isHost) {
        const current = hostValue.value || "";
        const p = current.split(":");
        if (ipPattern.test(p[0])) {
          ipValue.value = p[0];
          portValue.value = p[1] || "";
        } else {
          ipValue.value = "";
          portValue.value = "";
        }
      } else {
        // Switching to host → assemble current ip:port into the text field
        if (ipValue.value) {
          hostValue.value =
            props.showPort && portValue.value
              ? ipValue.value + ":" + portValue.value
              : ipValue.value;
        }
      }
      emitValue();
    });

    // Host text changes
    watch(hostValue, () => emitValue());

    // Port changes
    watch(portValue, () => emitValue());

    const onIpChange = (val) => {
      ipValue.value = val;
      emitValue();
    };

    return {
      useHostMode,
      hostValue,
      ipValue,
      portValue,
      onIpChange,
    };
  },
};
</script>

<style scoped lang="scss">
.host-ip-input {
  ::v-deep .custom-switch .custom-control-label {
    padding-left: 0 !important;
  }
}
</style>
